/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { AMEX, C2F } from './common';

import { NextRouter } from 'next/router';
import { JsonObject } from '@redventures/cohesion-utils-react';
import { UseFetch } from '~/hooks/useFetch';
import { DealBanner, VrcExperienceCheckoutBanner } from './plan';

export interface Disclosure {
  disclosureKey: string;
  name: string;
  text?: string;
  options: string[];
}

export interface NavBarProps {
  className: string;
  localeUtils: {
    getMonths: () => void;
  };
  month: Date;
  showNextButton: boolean;
  showPreviousButton: boolean;
  onPreviousClick: () => void;
  onNextClick: () => void;
}
interface Window {
  end: string;
  scheduleId: string;
  start: string;
}

export interface PIIFormProps {
  fundingAccountToken: string;
  ref: MutableRefObject<HTMLIFrameElement>;
  sendPostMessage: (token: string) => void;
  setFundingAccountToken: Dispatch<SetStateAction<string>>;
  src: string;
}

export interface UseOrderProps {
  activeStep: number;
  paperBill: boolean;
  totalUpfrontCharges?: number;
  nextEnabled: boolean;
  submitDisabled: boolean;
  filteredDisclosures: Disclosure[];
  handleOrderSubmit: () => void;
  handleSubmitCommercialModal: () => void;
  handleDisclosureCheckbox: (event) => void;
  setActiveStep: Dispatch<SetStateAction<number>>;
  handleAutopayClick: (checked: boolean) => void;
  handlePaper: (paperBill: boolean) => void;
  handlePaperless: (paperBill: boolean) => void;
  firebase?: any;
  iframes: {
    credit: PIIFormProps;
    debit: PIIFormProps;
    ach: PIIFormProps;
  };
  install: {
    data: InstallScheduleData;
    loading: boolean;
  };
  monarch: {
    [key: string]: string | number | boolean | string[] | Record<string, unknown>;
  };
  c2f?: C2F;
  amexOffer?: AMEX;
  isCopper?: boolean;
  installOptions?: {
    data: InstallOptionsData;
    error: Error | InstallOptionsData;
    loading: boolean;
    handleInstallOptions?: (installType: string) => Promise<void>;
  };
  paymentSession: {
    loading: boolean;
  };
  installationOptionsPending?: boolean;
  router: NextRouter;
}

interface Window {
  end: string;
  scheduleId: string;
  start: string;
}
export interface InstallScheduleData {
  appointmentMap?: {
    [key: string]: Window[];
  };
  success?: boolean;
}

export interface InstallOptionsData {
  success?: boolean;
}

export interface InstallOptionsFetchedProperties extends UseFetch<InstallOptionsData> {
  error?: Error | InstallOptionsData;
  loading: boolean;
  data?: InstallOptionsData;
  fetch: (options?: RequestInit) => Promise<InstallOptionsData>; // Ensure it returns a Promise<InstallOptionsData>
}

export interface InstallFetchedProperties {
  error?: InstallScheduleData | Error;
  loading: boolean;
  data?: InstallScheduleData;
  fetch: (options?: RequestInit) => void;
}

interface PaymentSessionFetchResponse {
  success?: boolean;
  sessionToken?: string;
}
export interface PaymentSessionFetchedProperties {
  error?: Error | PaymentSessionFetchResponse;
  loading?: boolean;
  data?: PaymentSessionFetchResponse;
  fetch: (options?: RequestInit) => Promise<PaymentSessionFetchResponse>;
}

// Redux
export enum ActionTypes {
  SET_SELECTED_TIME = 'time/set/selected',
  SET_SELECTED_DATE = 'date/set/selected',
}

export interface SelectedAppointment {
  scheduleId: string;
  startTime?: string;
  endTime?: string;
  timeFrame: string;
  date?: string;
}

export interface SelectedTimeAction {
  type: ActionTypes.SET_SELECTED_TIME;
  appointment: SelectedAppointment;
}

export interface SelectedDateAction {
  type: ActionTypes.SET_SELECTED_DATE;
  date: string;
}

export enum APOptions {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  ACH = 'ACH',
}

export type AutopayOptions = APOptions.CREDIT | APOptions.DEBIT | APOptions.ACH | null;

export type Actions = SelectedTimeAction | SelectedDateAction;

export interface InstallVrcConfig {
  enabled?: boolean;
  bannerCopy?: string;
  tooltipCopy?: string;
  quarterEndIcon?: string;
  quarterEndDate?: string;
}

export class AutoPayMessages {
  selectedCopy: string;
  deselectedCopy: string;
}

export interface OrderPageConfigMetadata extends JsonObject {
  title: {
    enabled: boolean;
    copy: string;
  };
  subtitle: {
    enabled: boolean;
    copy: string;
  };
  banner: {
    bannerEnabled: boolean;
    bannerSubtitle: string;
    bannerTitle: string;
    banner200: {
      enabled: boolean;
      banner200Title: string;
      banner200Subtitle: string;
    };
    banner500: {
      enabled: boolean;
      banner500Title: string;
      banner500Subtitle: string;
    };
    banner1000: {
      enabled: boolean;
      bannerGigTitle: string;
      bannerGigSubtitle: string;
    };
    banner2000: {
      enabled: boolean;
      banner2GigTitle: string;
      banner2GigSubtitle: string;
    };
    banner5000: {
      enabled: boolean;
      banner5GigTitle: string;
      banner5GigSubtitle: string;
    };
    banner7000: {
      enabled: boolean;
      banner7GigTitle: string;
      banner7GigSubtitle: string;
    };
  };
  countdownBanner: {
    enabled: boolean;
    tooltip: string;
    subText: string;
  };
  install: {
    calendarKey: {
      icon: string;
      title: string;
      subtitle: string;
    };
    selfInstall: {
      enabled: boolean;
      title: string;
      selected: string;
      label: string;
      copy: string;
      calendarCopy?: {
        calendarLabel?: string;
        calendarSubcopy?: string;
      };
    };
    expertInstall: {
      enabled?: boolean;
      title?: string;
      selected: string;
      label: string;
      copy: string;
      calendarCopy?: {
        calendarLabel?: string;
        calendarSubcopy?: string;
      };
    };
    instantActivation: {
      title?: string;
      selected?: boolean;
      label?: string;
      copy?: string;
      calendarCopy?: {
        topBannerCopy?: string;
        bottomBannerCopy?: string;
        equipmentRequiredCopy?: string;
        calendarSubcopy?: string;
      };
    };
  };
  installCalendar: {
    buttonText: string;
    calendarRedesignEnabled: boolean;
    calendarKey: {
      icon: string;
      title: string;
      subtitle: string;
    };
    tooltip: {
      enabled: boolean;
      title: string;
      tooltipText: string;
    };
  };
  phoneNumberHeader: {
    enabled: boolean;
  };
  installVrc?: InstallVrcConfig;
  vrcExperienceCheckoutBanner?: VrcExperienceCheckoutBanner;
  dealBanner?: DealBanner;
  autopay?: AutoPayMessages;
  ibcCountdownBanner: {
    enabled: boolean;
  };
  postCreditBanner: {
    copy: string;
    enabled: boolean;
    icon: string;
    theme: 'default' | 'blue';
  };
}
